// birthPlace.js — one definition of "this birth place is usable for a chart".
//
// The backend never stores a place without coordinates + timezone: every
// birth-details save geocodes server-side and otherwise answers
// 422 {"error": "place_unresolved"} (backend/utils/birthplace.py). The forms
// only enable submit once the place is resolved (a suggestion was picked, or
// /api/geo/resolve found it), and the dashboard shows "Add your birth place"
// for accounts saved before that rule existed (needs_birthplace).

export const PLACE_UNRESOLVED = 'place_unresolved';
// 503: the geocoder was busy/unavailable — the place was NOT checked. Retryable;
// never "not found".
export const PLACE_CHECK_BUSY = 'place_check_busy';
export const SAVE_FAILED_MESSAGE = 'Failed to save your details. Please try again.';
export const PICK_FROM_LIST_MESSAGE = 'Please choose your birth city from the list';
export const NEAREST_TOWN_MESSAGE = 'Village not listed? Try the nearest town.';
export const CHECK_FAILED_MESSAGE = "Couldn't check the place — tap to retry";
export const SESSION_EXPIRED_MESSAGE = 'Your session expired. Please sign in again.';

export const hasCoords = (lat, lon) => {
  if (lat === null || lat === undefined || lat === '' || lon === null || lon === undefined || lon === '') return false;
  const la = Number(lat);
  const lo = Number(lon);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return false;
  if (la === 0 && lo === 0) return false; // legacy "geocode failed" value
  return la >= -90 && la <= 90 && lo >= -180 && lo <= 180;
};

// True when the account's own chart cannot be computed for want of a place.
// A user object with no coordinate keys at all (partial / stale cache) is
// "unknown", not "missing": only an explicit null/empty counts, so a user who
// has coordinates is never told to add a place.
export const needsBirthplace = (user) => {
  if (!user) return false;
  if (user.needs_birthplace) return true;
  const place = (user.place || '').trim();
  if (!place) return false;
  if (user.latitude === undefined && user.longitude === undefined) return false;
  return !hasCoords(user.latitude, user.longitude);
};

const bodyOf = (errOrBody) => {
  if (!errOrBody) return null;
  const body = errOrBody.response ? errOrBody.response.data : errOrBody;
  return body && typeof body === 'object' ? body : null;
};

// 503 place_check_busy — accepts an axios error or a parsed body.
export const isPlaceCheckBusy = (errOrBody) => {
  const body = bodyOf(errOrBody);
  return !!body && body.error === PLACE_CHECK_BUSY;
};

// Classify a failed birth-details save (axios error, or a fetch status + body).
export const classifySaveFailure = (errOrBody) => ({
  saveFailed: true,
  placeUnresolved: isPlaceUnresolved(errOrBody),
  placeCheckBusy: isPlaceCheckBusy(errOrBody),
});

// The one message to show for a failed birth-details save.
export const saveFailureMessage = (result) => {
  if (result?.sessionExpired) return SESSION_EXPIRED_MESSAGE;
  if (result?.placeCheckBusy) return CHECK_FAILED_MESSAGE;
  if (result?.placeUnresolved) return PICK_FROM_LIST_MESSAGE;
  return SAVE_FAILED_MESSAGE;
};

// Accepts an axios error, a fetch-parsed body, or a status/body pair.
export const isPlaceUnresolved = (errOrBody) => {
  if (!errOrBody) return false;
  const body = errOrBody.response ? errOrBody.response.data : errOrBody;
  if (!body || typeof body !== 'object') return false;
  // Branch on the machine code in `error`; `detail` is a human sentence now.
  // (The session start's coords_missing 422 is the older {detail: {code}} shape.)
  const { error, detail } = body;
  return error === PLACE_UNRESOLVED
    || (!!detail && typeof detail === 'object' && detail.code === 'coords_missing');
};
